import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { listMySuppliers, type MySupplierDto } from '../api/mySuppliers'
import { listRestaurantRfqs } from '../api/rfqs'
import { rfqDisplayLabel } from '../api/rfqLabels'
import type { RfqDto } from '../api/types'
import { formatApiErrorMessage } from '../api/apiErrorMessage'
import { useAuth } from '../auth/AuthProvider'
import { standardPlanUiCopy, startPlanUiCopy } from '../billing/startPlanUiCopy'
import { useBuyerEntitlements } from '../hooks/useBuyerEntitlements'
import { usePlanGate } from '../hooks/usePlanGate'
import { useActiveVenue } from '../venues/ActiveVenueProvider'
import { useCart } from '../cart/CartProvider'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { StatTile, StatTileRow } from '../components/StatTile'
import { needsRestaurantAction } from './ordersUtils'
import {
  formatRfqDate,
  isArchivedRestaurantRfq,
  restaurantRfqStatusPresentation,
} from './rfqStatus'

const recentRestaurantRfqs = (rfqs: RfqDto[], limit = 5): RfqDto[] =>
  [...rfqs]
    .filter((rfq) => !isArchivedRestaurantRfq(rfq))
    .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())
    .slice(0, limit)

const topSuppliers = (suppliers: MySupplierDto[], limit = 5): MySupplierDto[] =>
  [...suppliers]
    .sort((left, right) => right.ordersCount - left.ordersCount || left.supplierName.localeCompare(right.supplierName, 'ru'))
    .slice(0, limit)

const formatPositionCount = (count: number): string => {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod100 >= 11 && mod100 <= 14) {
    return `${count} позиций`
  }
  if (mod10 === 1) {
    return `${count} позиция`
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return `${count} позиции`
  }
  return `${count} позиций`
}

const emptySuppliersPage = () => ({
  items: [] as MySupplierDto[],
  page: 0,
  size: 0,
  totalElements: 0,
  totalPages: 0,
})

export const RestaurantOverviewScreen = () => {
  const { session } = useAuth()
  const entitlements = useBuyerEntitlements()
  const uiCopy = entitlements.isStartPlan ? startPlanUiCopy : standardPlanUiCopy
  const { requestFeature, paywallModal } = usePlanGate()
  const navigate = useNavigate()
  const { activeVenueId } = useActiveVenue()
  const { lineCount } = useCart()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openCount, setOpenCount] = useState(0)
  const [actionCount, setActionCount] = useState(0)
  const [actionRfqs, setActionRfqs] = useState<{ id: number; title: string }[]>([])
  const [recentRfqs, setRecentRfqs] = useState<RfqDto[]>([])
  const [topSupplierRows, setTopSupplierRows] = useState<MySupplierDto[]>([])

  useEffect(() => {
    if (!session) {
      return
    }
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const rfqsPromise = entitlements.platformRfqEnabled
          ? listRestaurantRfqs(session.organizationId, activeVenueId)
          : Promise.resolve([] as RfqDto[])
        const suppliersPromise = entitlements.buyerSuppliersEnabled
          ? listMySuppliers(session.organizationId, 0, 50)
          : Promise.resolve(emptySuppliersPage())

        const [rfqs, suppliersPage] = await Promise.all([rfqsPromise, suppliersPromise])
        const open = rfqs.filter((rfq) => rfq.status === 'open')
        const needsAction = rfqs.filter(needsRestaurantAction)
        setOpenCount(open.length)
        setActionCount(needsAction.length)
        setActionRfqs(needsAction.slice(0, 5).map((rfq) => ({ id: rfq.id, title: rfq.title })))
        setRecentRfqs(recentRestaurantRfqs(rfqs))
        setTopSupplierRows(topSuppliers(suppliersPage.items))
      } catch (loadError) {
        setError(formatApiErrorMessage(loadError, 'Не удалось загрузить сводку'))
      } finally {
        setLoading(false)
      }
    })()
  }, [
    activeVenueId,
    entitlements.buyerSuppliersEnabled,
    entitlements.platformRfqEnabled,
    session,
  ])

  const actionLink =
    actionRfqs.length === 1
      ? `/orders?rfqId=${actionRfqs[0].id}`
      : '/orders?filter=action'

  const hasPriorityWork = actionCount > 0

  return (
    <section className="market-page market-page--overview">
      <MarketPageHeader
        title="Обзор"
        description={
          entitlements.isStartPlan
            ? 'На «Старте» работайте через «Мои прайсы», корзину и бланк заказа. Заявки на платформе — на тарифах «Стандарт» и «Премиум».'
            : 'Актуальные заявки, поставщики и то, что требует вашего ответа.'
        }
        actions={
          hasPriorityWork ? (
            <Link to={actionLink} className="echat-btn echat-btn--primary echat-btn--sm">
              Открыть заявки
            </Link>
          ) : entitlements.isStartPlan ? (
            <Link to="/my-prices" className="echat-btn echat-btn--primary echat-btn--sm">
              Мои прайсы
            </Link>
          ) : lineCount > 0 ? (
            <button
              type="button"
              className="echat-btn echat-btn--primary echat-btn--sm"
              onClick={() => {
                if (requestFeature('platform_rfq')) {
                  navigate('/orders/create')
                }
              }}
            >
              {uiCopy.overviewCartActionLabel}
            </button>
          ) : undefined
        }
      />

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && !error ? (
        <>
          {hasPriorityWork ? (
            <section className="market-overview-priority" aria-label="Требует ответа">
              <div className="market-overview-priority__inner">
                <div className="market-overview-priority__content">
                  <h2 className="market-overview-priority__title">Заявки ждут ответа</h2>
                  <p className="market-overview-priority__text">
                    {actionCount === 1
                      ? 'По одной заявке нужно подтвердить замену или выполнение.'
                      : `По ${actionCount} заявкам нужно подтвердить замену или выполнение.`}
                  </p>
                </div>
              </div>
            </section>
          ) : null}

          <StatTileRow className="market-overview-metrics">
            {entitlements.platformRfqEnabled ? (
              <>
                <StatTile label="Открытых заявок" value={openCount} />
                <StatTile label="Требуют ответа" value={actionCount} emphasis={actionCount > 0} />
              </>
            ) : null}
            {lineCount > 0 ? (
              <StatTile
                label="В корзине"
                value={lineCount}
                hint={`${formatPositionCount(lineCount)} — ${uiCopy.overviewCartHint}`}
                emphasis
                cta={
                  hasPriorityWork ? undefined : entitlements.isStartPlan ? (
                    <Link to="/cart" className="echat-btn echat-btn--primary echat-btn--sm market-stat-tile__cta">
                      {uiCopy.overviewCartActionLabel}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="echat-btn echat-btn--primary echat-btn--sm market-stat-tile__cta"
                      onClick={() => {
                        if (requestFeature('platform_rfq')) {
                          navigate('/orders/create')
                        }
                      }}
                    >
                      {uiCopy.overviewCartActionLabel}
                    </button>
                  )
                }
              />
            ) : entitlements.isStartPlan ? (
              <StatTile
                label="Корзина пуста"
                value="—"
                hint={uiCopy.overviewRecentRfqsEmpty}
                cta={
                  <Link to="/my-prices" className="echat-btn echat-btn--ghost echat-btn--sm market-stat-tile__cta">
                    Открыть «Мои прайсы»
                  </Link>
                }
              />
            ) : null}
          </StatTileRow>

          {entitlements.platformRfqEnabled && actionRfqs.length > 0 ? (
            <article className="echat-panel">
              <h2 className="echat-panel__subtitle">Нужен ответ</h2>
              <ul className="market-overview-list">
                {actionRfqs.map((rfq) => (
                  <li key={rfq.id}>
                    <Link to={`/orders?rfqId=${rfq.id}`} className="market-overview-link">
                      {rfq.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </article>
          ) : null}

          <div className="market-overview-preview-grid">
            <article className="echat-panel">
              <div className="market-overview-panel__header">
                <h2 className="echat-panel__subtitle">Последние заявки</h2>
                {entitlements.platformRfqEnabled ? (
                  <Link to="/orders" className="echat-btn echat-btn--ghost echat-btn--sm">
                    Все заявки
                  </Link>
                ) : (
                  <Link to="/my-prices" className="echat-btn echat-btn--ghost echat-btn--sm">
                    Мои прайсы
                  </Link>
                )}
              </div>
              <div className="market-overview-panel__body">
                {recentRfqs.length === 0 ? (
                  <p className="echat-muted">{uiCopy.overviewRecentRfqsEmpty}</p>
                ) : (
                  <div className="market-table-wrap market-table-wrap--cards">
                    <table className="market-table market-table--cards">
                    <thead>
                      <tr>
                        <th>Заявка</th>
                        <th>Статус</th>
                        <th>Обновлена</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentRfqs.map((rfq) => {
                        const status = restaurantRfqStatusPresentation(rfq)
                        return (
                          <tr key={rfq.id}>
                            <td data-label="Заявка" className="market-table__cell--truncate market-table__cell--primary">
                              <Link to={`/orders?rfqId=${rfq.id}`} className="market-overview-link">
                                {rfqDisplayLabel(rfq)}
                              </Link>
                            </td>
                            <td data-label="Статус">
                              <span className={status.badgeClass}>{status.label}</span>
                            </td>
                            <td data-label="Обновлена">{formatRfqDate(rfq.updatedAt)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                    </table>
                  </div>
                )}
              </div>
            </article>

            <article className="echat-panel">
              <div className="market-overview-panel__header">
                <h2 className="echat-panel__subtitle">Частые поставщики</h2>
                {entitlements.buyerSuppliersEnabled ? (
                  <Link to="/suppliers" className="echat-btn echat-btn--ghost echat-btn--sm">
                    Все поставщики
                  </Link>
                ) : (
                  <Link to="/my-prices" className="echat-btn echat-btn--ghost echat-btn--sm">
                    Мои прайсы
                  </Link>
                )}
              </div>
              <div className="market-overview-panel__body">
                {topSupplierRows.length === 0 ? (
                  <p className="echat-muted">{uiCopy.overviewSuppliersEmpty}</p>
                ) : (
                  <div className="market-table-wrap market-table-wrap--cards">
                    <table className="market-table market-table--cards">
                    <thead>
                      <tr>
                        <th>Поставщик</th>
                        <th>Заказов</th>
                        <th>Контакт</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSupplierRows.map((supplier) => (
                        <tr key={supplier.supplierOrganizationId}>
                          <td data-label="Поставщик" className="market-table__cell--truncate market-table__cell--primary">{supplier.supplierName}</td>
                          <td data-label="Заказов">{supplier.ordersCount}</td>
                          <td data-label="Контакт" className="market-table__cell--truncate">
                            {supplier.email ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    </table>
                  </div>
                )}
              </div>
            </article>
          </div>
        </>
      ) : null}
      {paywallModal}
    </section>
  )
}
